import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/App';
import { useConfig } from '@/context/ConfigContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import { 
  Users, TrendingUp, AlertTriangle, Target, RefreshCw, Brain, 
  Sparkles, ArrowUp, ArrowDown, Clock, Building, Loader2,
  UserPlus, Activity, Zap, ChevronRight, CalendarDays, Briefcase
} from 'lucide-react';
import { 
  PageHeader, MetricCard, StatusBadge, TeamBadge, DisciplineBadge, 
  GradientProgress, ModernCard, SectionHeader,
  modernTabsClass, modernTabTriggerClass 
} from '@/components/ModernUI';
import { InsightsExport } from '@/components/InsightsExport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  HIGH: { bg: 'bg-gradient-to-r from-rose-500 to-red-600', border: 'border-red-300' },
  MEDIUM: { bg: 'bg-gradient-to-r from-amber-500 to-orange-600', border: 'border-amber-300' },
  LOW: { bg: 'bg-gradient-to-r from-emerald-500 to-green-600', border: 'border-green-300' }
};

export default function HiringPredictions() {
  const { token } = useAuth();
  const { getRNCMCaseloadTarget, getTargetADC, getWorkforceTarget } = useConfig();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [feedData, setFeedData] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [latestPrediction, setLatestPrediction] = useState(null);
  const [projectedCensus, setProjectedCensus] = useState('');
  const [growthRate, setGrowthRate] = useState('10');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Get configurable targets from context
  const caseloadTarget = useMemo(() => getRNCMCaseloadTarget(), [getRNCMCaseloadTarget]);
  const targetADC = useMemo(() => getTargetADC(), [getTargetADC]);
  const rncmFinalTarget = useMemo(() => getWorkforceTarget('rncm_final_target', 35), [getWorkforceTarget]);
  
  const fetchData = async () => {
    try {
      // Fetch from the new integrated feed endpoint
      const [feedRes, predictionsRes] = await Promise.all([
        fetch(`${API_URL}/api/capacity-projection/feed/hiring-predictions?growth_rate=${growthRate || 0}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/hiring/predictions?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null)
      ]);

      if (feedRes.ok) {
        const data = await feedRes.json();
        setFeedData(data);
      }

      if (predictionsRes?.ok) {
        const data = await predictionsRes.json();
        setPredictions(data);
        if (data.length > 0) {
          setLatestPrediction(data[0]);
        }
      }
    } catch (error) {
      console.error('Error loading hiring data:', error);
      toast.error('Failed to load hiring predictions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleRefreshWithGrowth = () => {
    setLoading(true);
    fetchData();
    toast.success('Data refreshed with new growth rate');
  };

  const handleGeneratePrediction = async () => {
    setGenerating(true);
    try {
      const params = new URLSearchParams();
      if (projectedCensus) params.append('projected_census', projectedCensus);
      
      const url = `${API_URL}/api/hiring/predict-with-census${params.toString() ? '?' + params.toString() : ''}`;
        
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const prediction = await response.json();
        toast.success('Hiring prediction generated');
        setLatestPrediction(prediction);
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to generate prediction', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error generating prediction');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const currentState = feedData?.data?.current_state || {};
  const hiringNeeds = feedData?.data?.hiring_needs || [];
  const totalFteRecommended = feedData?.data?.total_fte_recommended || 0;
  const immediateHires = feedData?.data?.immediate_hires_needed || 0;

  // Chart data for discipline distribution
  const disciplineData = Object.entries(currentState.by_discipline || {}).map(([disc, data]) => ({
    name: disc,
    count: data.count,
    utilization: data.utilization
  }));

  // Trend data from legacy predictions
  const trendData = [...predictions].reverse().map(p => ({
    date: p.prediction_date,
    current: p.current_rncm_count,
    optimal: p.optimal_rncm_count,
    gap: p.hiring_gap
  }));

  return (
    <div className="space-y-6" data-testid="hiring-predictions-page">
      {/* Modern Gradient Header */}
      <PageHeader
        icon={UserPlus}
        title="Hiring Predictions"
        subtitle="AI-powered staffing forecasts from Capacity Projection data"
        gradient="from-[#00b0ab] via-[#009590] to-[#48494b]"
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5">
            <Label className="text-white text-xs">Growth %</Label>
            <Input
              type="number"
              className="w-16 h-7 text-xs bg-white/20 border-white/30 text-white placeholder-white/70"
              value={growthRate}
              onChange={(e) => setGrowthRate(e.target.value)}
              placeholder="10"
            />
          </div>
          <Button 
            variant="secondary"
            className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
            onClick={handleRefreshWithGrowth}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Update
          </Button>
          <Button 
            className="bg-white text-[#00b0ab] hover:bg-white/90"
            onClick={handleGeneratePrediction}
            disabled={generating}
          >
            {generating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            AI Forecast
          </Button>
        </div>
      </PageHeader>

      {/* Data Source Indicator */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Zap className="h-4 w-4 text-emerald-500" />
        <span>Data integrated from Capacity Projection</span>
        <span className="text-xs">• Growth scenario: {growthRate}%</span>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Total Staff"
          value={currentState.total_staff || 0}
          icon={Users}
          gradient="indigo"
          subtitle="Current workforce"
        />
        <MetricCard
          title="Utilization"
          value={`${currentState.overall_utilization?.toFixed(0) || 0}%`}
          icon={Activity}
          gradient={currentState.overall_utilization >= 90 ? 'rose' : currentState.overall_utilization >= 80 ? 'amber' : 'emerald'}
          subtitle="Overall capacity usage"
        />
        <MetricCard
          title="Total Capacity"
          value={currentState.total_capacity || 0}
          icon={Target}
          gradient="blue"
          subtitle="Weekly visits available"
        />
        <MetricCard
          title="FTE Needed"
          value={totalFteRecommended.toFixed(1)}
          icon={UserPlus}
          gradient={totalFteRecommended > 0 ? 'amber' : 'emerald'}
          subtitle="Recommended hires"
        />
        <MetricCard
          title="Immediate Hires"
          value={immediateHires}
          icon={AlertTriangle}
          gradient={immediateHires > 0 ? 'rose' : 'emerald'}
          subtitle="Urgent positions"
        />
      </div>

      {/* Hiring Needs Alert */}
      {hiringNeeds.length > 0 && hiringNeeds.some(h => h.priority === 'HIGH') && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-amber-500 rounded-xl">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-amber-800 dark:text-amber-200">Staffing Action Required</h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {hiringNeeds.filter(h => h.priority === 'HIGH').length} high-priority hiring needs identified
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {hiringNeeds.filter(h => h.priority === 'HIGH').slice(0, 3).map((need, idx) => (
                    <Badge key={idx} className="bg-amber-500 text-white">
                      {need.fte_needed} {need.discipline} - {need.timeline}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="overview" className={modernTabTriggerClass}>
            <Activity className="h-4 w-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="needs" className={modernTabTriggerClass}>
            <Briefcase className="h-4 w-4 mr-2" /> Hiring Needs
          </TabsTrigger>
          <TabsTrigger value="disciplines" className={modernTabTriggerClass}>
            <Users className="h-4 w-4 mr-2" /> By Discipline
          </TabsTrigger>
          <TabsTrigger value="ai" className={modernTabTriggerClass}>
            <Brain className="h-4 w-4 mr-2" /> AI Forecast
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Capacity Overview */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Target} title="Capacity Status" subtitle="Current workforce utilization" />
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Visits</p>
                      <p className="text-3xl font-bold">{currentState.total_visits}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Max Capacity</p>
                      <p className="text-3xl font-bold text-emerald-600">{currentState.total_capacity}</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Utilization</span>
                      <span className={currentState.overall_utilization >= 90 ? 'text-red-600 font-bold' : currentState.overall_utilization >= 80 ? 'text-amber-600' : 'text-emerald-600'}>
                        {currentState.overall_utilization?.toFixed(1)}%
                      </span>
                    </div>
                    <GradientProgress value={currentState.overall_utilization || 0} max={100} showLabel={false} />
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-600">{currentState.overall_utilization >= 90 ? 'Critical' : currentState.overall_utilization >= 80 ? 'Tight' : 'Healthy'}</p>
                      <p className="text-xs text-muted-foreground">Capacity Status</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{currentState.total_capacity - currentState.total_visits}</p>
                      <p className="text-xs text-muted-foreground">Available Visits</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </ModernCard>

            {/* Discipline Distribution */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Users} title="Staff by Discipline" />
              </CardHeader>
              <CardContent>
                {disciplineData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={disciplineData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                      <YAxis type="category" dataKey="name" width={80} />
                      <Tooltip formatter={(value, name) => name === 'utilization' ? `${value}%` : value} />
                      <Bar dataKey="utilization" name="Utilization %" fill="#6366F1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                    <p>No discipline data available</p>
                  </div>
                )}
              </CardContent>
            </ModernCard>
          </div>

          {/* Trend Chart */}
          {trendData.length > 0 && (
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={TrendingUp} title="Historical Hiring Trends" />
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="current" 
                      name="Current Staff" 
                      stroke="#6366F1" 
                      fill="#6366F1" 
                      fillOpacity={0.3} 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="optimal" 
                      name="Optimal Staff" 
                      stroke="#10B981" 
                      fill="#10B981" 
                      fillOpacity={0.3} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="gap" 
                      name="Hiring Gap" 
                      stroke="#EF4444" 
                      strokeWidth={2} 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </ModernCard>
          )}
        </TabsContent>

        {/* Hiring Needs Tab */}
        <TabsContent value="needs">
          <ModernCard>
            <CardHeader>
              <CardTitle>Prioritized Hiring Needs</CardTitle>
              <CardDescription>Based on capacity projection and {growthRate}% growth scenario</CardDescription>
            </CardHeader>
            <CardContent>
              {hiringNeeds.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No Hiring Needs Identified</p>
                  <p className="text-sm mt-2">Current staffing levels are adequate for projected demand</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {hiringNeeds.map((need, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-xl border-l-4 ${PRIORITY_COLORS[need.priority]?.border || 'border-gray-300'} bg-slate-50 dark:bg-slate-800/50`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <StatusBadge status={need.priority} size="sm" />
                            <span className="font-semibold">{need.discipline}</span>
                            <Badge variant="outline">{need.fte_needed} FTE</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{need.reason}</p>
                          {need.note && (
                            <p className="text-xs text-muted-foreground mt-1 italic">{need.note}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-sm">
                            <CalendarDays className="h-4 w-4" />
                            <span>{need.timeline}</span>
                          </div>
                          {need.visits_gap > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">{need.visits_gap} visits gap</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* By Discipline Tab */}
        <TabsContent value="disciplines">
          <ModernCard>
            <CardHeader>
              <CardTitle>Discipline Breakdown</CardTitle>
              <CardDescription>Staff distribution and utilization by role</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead>Discipline</TableHead>
                    <TableHead>Staff Count</TableHead>
                    <TableHead>Utilization</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(currentState.by_discipline || {}).map(([disc, data]) => (
                    <TableRow key={disc}>
                      <TableCell>
                        <DisciplineBadge discipline={disc} />
                      </TableCell>
                      <TableCell className="font-medium">{data.count}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <GradientProgress value={data.utilization} max={100} showLabel={false} />
                          <span className="text-sm">{data.utilization?.toFixed(0)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge 
                          status={data.utilization >= 90 ? 'CRITICAL' : data.utilization >= 80 ? 'HIGH' : data.utilization >= 60 ? 'MODERATE' : 'LOW'} 
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* AI Forecast Tab */}
        <TabsContent value="ai">
          <ModernCard>
            <CardHeader>
              <div className="flex items-center justify-between">
                <SectionHeader 
                  icon={Brain} 
                  title="AI Hiring Recommendations" 
                  subtitle="Intelligent analysis based on capacity and growth projections"
                />
                <div className="flex items-center gap-2">
                  {latestPrediction?.ai_analysis && (
                    <InsightsExport 
                      insights={[
                        {
                          title: "Hiring Recommendations Analysis",
                          description: latestPrediction.ai_analysis,
                          severity: immediateHires > 0 ? "critical" : hiringNeeds.length > 0 ? "warning" : "info",
                          category: "Workforce Planning",
                          recommendation: immediateHires > 0 
                            ? `${immediateHires} immediate hires recommended` 
                            : `${totalFteRecommended} total FTE recommended`
                        },
                        ...hiringNeeds.map(need => ({
                          title: `${need.discipline} Hiring Need`,
                          description: need.reason,
                          severity: need.priority.toLowerCase(),
                          category: "Hiring Priority",
                          recommendation: `${need.fte_needed} FTE needed - Timeline: ${need.timeline}`
                        }))
                      ]}
                      pageTitle="Hiring Predictions"
                      data-testid="export-hiring-analysis"
                    />
                  )}
                  <Button 
                    onClick={handleGeneratePrediction}
                    disabled={generating}
                    className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
                  >
                    {generating ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    Generate AI Analysis
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {latestPrediction?.ai_analysis ? (
                <ScrollArea className="h-[400px] rounded-lg border p-4 bg-muted/30">
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown
                      components={{
                        h1: ({children}) => <h1 className="text-xl font-bold mt-4 mb-2 text-[#00b0ab] dark:text-[#33c3bf]">{children}</h1>,
                        h2: ({children}) => <h2 className="text-lg font-bold mt-4 mb-2 text-[#00b0ab] dark:text-[#33c3bf]">{children}</h2>,
                        h3: ({children}) => <h3 className="text-base font-semibold mt-3 mb-1 text-[#00b0ab] dark:text-[#33c3bf]">{children}</h3>,
                        p: ({children}) => <p className="my-2 leading-relaxed">{children}</p>,
                        ul: ({children}) => <ul className="list-disc ml-4 my-2 space-y-1">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal ml-4 my-2 space-y-1">{children}</ol>,
                        li: ({children}) => <li className="leading-relaxed">{children}</li>,
                        strong: ({children}) => <strong className="font-semibold text-foreground">{children}</strong>,
                      }}
                    >
                      {latestPrediction.ai_analysis}
                    </ReactMarkdown>
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">Generate AI Analysis</p>
                  <p className="text-sm mt-2">Get intelligent staffing recommendations based on current capacity data</p>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
